import { createSlice } from "@reduxjs/toolkit";
import { sortValuesByField } from "../../../../_services/sorting-helper";

const initialState = {
    loading: true,
    errorMessage: null,
    warningMessage: null,
    billingConfiguration: null,

    modalErrorMessage: null,
    modalWarningMessage: null,
    modalSuccessMessage: null,

    connectionsModal: false,
    connectionsModalLoading: false,
    activationLoadingId: null,
    connectionsModalErrorMessage: null,
    availableOrderInvoicingClients: null,

    activateBusinessEntityLoading: false,
    activateBusinessEntityErrorMessage: null,
    businessEntityActivationMode: null,
    businessEntityActivationValid: true,

    deactivationModal: false,
    deactivationLoading: false,
    deactivationErrorMessage: null,
    configurationToDeactivate: null,

    disableTestModeModal: false,
    disableTestModeLoading: false,
    disableTestModeErrorMessage: null,
    configurationToDisableTestMode: null,

    disableLiveModeModal: false,
    disableLiveModeLoading: false,
    disableLiveModeErrorMessage: null,
    configurationToDisableLiveMode: null,

    preferencesModal: false,
    modalLoading: false,
    updateLoading: false,

    editConfiguration: null,
    logoToUpload: null,
    unsavedChanges: false,

    currentPaymentMethods: [],
    paymentMethodsLoading: false,
    paymentMethodsErrorMessage: null,

    documentPreviewImage: null,
    documentPreviewErrorMessage: null,
    documentPreviewLoading: false,
    lastRequestId: null,

    deleteApiKeyLoading: {},
    deleteApiKeyErrorMessage: null,
    createApiKeyLoading: false,
    createApiKeyErrorMessage: null,

    portalConfiguration: null,
    portalConfigLoading: false,
    portalConfigErrorMessage: null,
    portalConfigSuccessMessage: null,

    portalBackgroundImageLoading: false,
    portalBackgroundImageErrorMessage: null,
    uploadedPortalBackgroundImageFilename: null,
};

const mergeOrderInvoicingConfiguration = (state, configuration) => {
    let billingConfiguration = state.billingConfiguration;
    let associatedInvoicingConfigurations = billingConfiguration.associatedInvoicingConfigurations;

    let index = associatedInvoicingConfigurations.findIndex((item) => {
        return item.externalUUID === configuration.externalUUID;
    });
    if (index > -1) {
        associatedInvoicingConfigurations[index] = configuration;
    } else {
        associatedInvoicingConfigurations.push(configuration);
        associatedInvoicingConfigurations = sortValuesByField(
            associatedInvoicingConfigurations,
            "associatedPlatformClientName"
        );
    }

    state.billingConfiguration = {
        ...state.billingConfiguration,
        associatedInvoicingConfigurations: associatedInvoicingConfigurations,
    };
};

const mergeLogoIntoOrderInvoicingConfiguration = (state, configuration) => {
    let billingConfiguration = state.billingConfiguration;
    let associatedInvoicingConfigurations = billingConfiguration.associatedInvoicingConfigurations;

    let index = associatedInvoicingConfigurations.findIndex((item) => {
        return item.externalUUID === configuration.externalUUID;
    });
    if (index > -1) {
        // First update the original OrderInvoicingConfiguration's logos
        let tempConfig = { ...associatedInvoicingConfigurations[index] };
        tempConfig.languageSetups = [...tempConfig.languageSetups];
        for (let i = 0; i < tempConfig.languageSetups.length; i++) {
            const updatedConfigLanguageIndex = configuration.languageSetups.findIndex((item) => {
                return item.language === tempConfig.languageSetups[i].language;
            });
            tempConfig.languageSetups[i] = {
                ...tempConfig.languageSetups[i],
                uploadedLogoFile:
                    updatedConfigLanguageIndex > -1
                        ? configuration.languageSetups[updatedConfigLanguageIndex].uploadedLogoFile
                        : tempConfig.languageSetups[i].uploadedLogoFile,
            };
        }
        associatedInvoicingConfigurations[index] = { ...tempConfig };

        // Now update the editConfiguration's logos
        tempConfig = { ...state.editConfiguration };
        tempConfig.languageSetups = [...tempConfig.languageSetups];
        for (let i = 0; i < tempConfig.languageSetups.length; i++) {
            const updatedConfigLanguageIndex = configuration.languageSetups.findIndex((item) => {
                return item.language === tempConfig.languageSetups[i].language;
            });
            tempConfig.languageSetups[i] = {
                ...tempConfig.languageSetups[i],
                uploadedLogoFile:
                    updatedConfigLanguageIndex > -1
                        ? configuration.languageSetups[updatedConfigLanguageIndex].uploadedLogoFile
                        : tempConfig.languageSetups[i].uploadedLogoFile,
            };
        }
        state.editConfiguration = { ...tempConfig };
    } else {
        state.editConfiguration = { ...configuration };
        associatedInvoicingConfigurations.push(configuration);
        associatedInvoicingConfigurations = sortValuesByField(
            associatedInvoicingConfigurations,
            "associatedPlatformClientName"
        );
    }

    state.billingConfiguration = {
        ...state.billingConfiguration,
        associatedInvoicingConfigurations: associatedInvoicingConfigurations,
    };
};

const pathwayBillingConfigurationSlice = createSlice({
    name: "PathwayBillingConfiguration",
    initialState,
    reducers: {
        setHasChanges(state, action) {
            state.hasChanges = action.payload;
        },
        setEditConfiguration(state, action) {
            state.editConfiguration = action.payload;
        },
        setUnsavedChanges(state, action) {
            state.unsavedChanges = action.payload;
        },

        createNewApiKeyRequest(state) {
            state.createApiKeyLoading = true;
            state.createApiKeyErrorMessage = null;
        },
        createNewApiKeySuccess(state, action) {
            state.createApiKeyLoading = false;
            mergeOrderInvoicingConfiguration(state, action.payload);
            state.editConfiguration = action.payload;
        },
        createNewApiKeyFailure(state, action) {
            state.createApiKeyLoading = false;
            state.createApiKeyErrorMessage = action.payload;
        },
        deleteApiKeyRequest(state, action) {
            if (!state.deleteApiKeyLoading) {
                state.deleteApiKeyLoading = {};
            }
            state.deleteApiKeyLoading[action.payload] = true;
            state.deleteApiKeyErrorMessage = null;
        },
        deleteApiKeySuccess(state, action) {
            if (!state.deleteApiKeyLoading) {
                state.deleteApiKeyLoading = {};
            }
            state.deleteApiKeyLoading[action.payload.apiKeyUUID] = false;
            mergeOrderInvoicingConfiguration(state, action.payload.configuration);
            state.editConfiguration = action.payload.configuration;
        },
        deleteApiKeyFailure(state, action) {
            if (!state.deleteApiKeyLoading) {
                state.deleteApiKeyLoading = {};
            }
            state.deleteApiKeyLoading[action.payload.apiKeyUUID] = false;
            state.deleteApiKeyErrorMessage = action.payload.errorMessage;
        },

        setLogoToUpload(state, action) {
            if (!state.logoToUpload) {
                state.logoToUpload = {};
            }
            state.logoToUpload[action.payload.language] = action.payload.logo;
        },
        getBillingConfigurationRequest(state) {
            state.loading = true;
            state.errorMessage = null;
            state.warningMessage = null;
            state.billingConfiguration = null;
        },
        getBillingConfigurationSuccess(state, action) {
            state.loading = false;
            state.billingConfiguration = action.payload.response;
            state.warningMessage = action.payload.warningMessage;
        },
        getBillingConfigurationFailure(state, action) {
            state.loading = false;
            state.errorMessage = action.payload;
            state.warningMessage = action.payload.warningMessage;
        },
        getBillingConfigurationForModalRequest(state) {
            state.modalLoading = true;
            state.modalErrorMessage = null;
        },
        getBillingConfigurationForModalSuccess(state, action) {
            state.modalLoading = false;
            state.billingConfiguration = action.payload.response;
            state.modalWarningMessage = action.payload.warningMessage;
        },
        getBillingConfigurationForModalFailure(state, action) {
            state.modalLoading = false;
            state.modalErrorMessage = action.payload;
        },
        getAvailableOrderInvoicingClientsRequest(state) {
            state.connectionsModalLoading = true;
            state.connectionsModalErrorMessage = null;
        },
        getAvailableOrderInvoicingClientsSuccess(state, action) {
            state.connectionsModalLoading = false;
            state.connectionsModalErrorMessage = null;
            state.availableOrderInvoicingClients = action.payload;
        },
        getAvailableOrderInvoicingClientsFailure(state, action) {
            state.connectionsModalLoading = false;
            state.connectionsModalErrorMessage = action.payload;
        },
        activateBusinessEntityConfigurationRequest(state) {
            state.activateBusinessEntityLoading = true;
            state.activateBusinessEntityErrorMessage = null;
        },
        activateBusinessEntityConfigurationSuccess(state) {
            state.activateBusinessEntityLoading = false;
        },
        activateBusinessEntityConfigurationFailure(state, action) {
            state.activateBusinessEntityLoading = false;
            state.activateBusinessEntityErrorMessage = action.payload;
        },
        clearActivateBusinessEntityError(state) {
            state.activateBusinessEntityErrorMessage = null;
        },
        setBusinessEntityActivationMode(state, action) {
            state.businessEntityActivationMode = action.payload;
        },
        setBusinessEntityActivationValid(state, action) {
            state.businessEntityActivationValid = action.payload;
        },
        activateOrderInvoicingConfigurationForClientRequest(state, action) {
            state.activationLoadingId = action.payload;
            state.connectionsModalErrorMessage = null;
        },
        activateOrderInvoicingConfigurationForClientSuccess(state, action) {
            state.activationLoadingId = null;
            state.availableOrderInvoicingClients = action.payload;
        },
        activateOrderInvoicingConfigurationForClientFailure(state, action) {
            state.activationLoadingId = null;
            state.connectionsModalErrorMessage = action.payload;
        },
        deactivateOrderInvoicingConfigurationForClientRequest(state) {
            state.deactivationLoading = true;
            state.deactivationErrorMessage = null;
        },
        deactivateOrderInvoicingConfigurationForClientSuccess(state, action) {
            state.deactivationLoading = false;
            state.deactivationModal = false;
            state.configurationToDeactivate = null;
            state.availableOrderInvoicingClients = action.payload;
        },
        deactivateOrderInvoicingConfigurationForClientFailure(state, action) {
            state.deactivationLoading = false;
            state.deactivationErrorMessage = action.payload;
        },
        updateOrderInvoicingConfigurationRequest(state) {
            state.updateLoading = true;
            state.modalErrorMessage = null;
            state.modalWarningMessage = null;
        },
        updateOrderInvoicingConfigurationSuccess(state, action) {
            state.updateLoading = false;
            state.modalSuccessMessage = "Speichern erfolgreich";
            mergeOrderInvoicingConfiguration(state, action.payload.response);
            state.modalWarningMessage = action.payload.warningMessage;
            state.editConfiguration = action.payload.response;
            state.logoToUpload = null;
            state.hasChanges = false;
        },
        updateOrderInvoicingConfigurationFailure(state, action) {
            state.updateLoading = false;
            state.modalErrorMessage = action.payload;
        },
        clearModalSuccess(state) {
            state.modalSuccessMessage = null;
        },
        uploadLogoRequest(state) {
            state.modalErrorMessage = null;
            state.updateLoading = true;
        },
        uploadLogoSuccess(state, action) {
            mergeLogoIntoOrderInvoicingConfiguration(state, action.payload.configuration);
            state.updateLoading = false;
            state.logoToUpload[action.payload.language] = null;
        },
        uploadLogoFailure(state, action) {
            state.modalErrorMessage = action.payload;
            state.updateLoading = false;
        },
        toggleOrderInvoicingConfigurationPreferencesModal(state, action) {
            state.modalErrorMessage = null;
            state.modalWarningMessage = null;
            state.modalSuccessMessage = null;
            state.modalLoading = false;
            state.preferencesModal = !state.preferencesModal;
            state.editConfiguration = null;
            state.businessEntityActivationMode = null;
            state.businessEntityActivationValid = true;
            state.activateBusinessEntityErrorMessage = null;
            if (state.documentPreviewImage && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
                URL.revokeObjectURL(state.documentPreviewImage);
            }
            state.documentPreviewImage = null;
            state.documentPreviewErrorMessage = null;
            state.documentPreviewLoading = false;
            state.lastRequestId = null;
            state.portalConfiguration = null;
            state.portalConfigLoading = false;
            state.portalConfigErrorMessage = null;
            state.portalConfigSuccessMessage = null;
            state.uploadedPortalBackgroundImageFilename = null;
        },
        toggleActivateChargingModal(state) {
            state.activateChargingModal = !state.activateChargingModal;
        },
        toggleConnectionsModal(state) {
            state.connectionsModal = !state.connectionsModal;
            state.connectionsModalErrorMessage = null;
            state.connectionsModalLoading = false;
            state.activationLoadingId = null;
        },
        toggleConfirmDeactivationModal(state, action) {
            state.deactivationModal = !state.deactivationModal;
            state.deactivationErrorMessage = null;
            state.deactivationLoading = false;
            state.configurationToDeactivate = action.payload;
        },
        toggleDisableTestModeModal(state, action) {
            state.disableTestModeModal = !state.disableTestModeModal;
            state.disableTestModeLoading = false;
            state.disableTestModeErrorMessage = null;
            state.configurationToDisableTestMode = action.payload;
        },
        toggleDisableLiveModeModal(state, action) {
            state.disableLiveModeModal = !state.disableLiveModeModal;
            state.disableLiveModeLoading = false;
            state.disableLiveModeErrorMessage = null;
            state.configurationToDisableLiveMode = action.payload;
        },
        getPaymentMethodsRequest(state) {
            state.paymentMethodsLoading = true;
            state.paymentMethodsErrorMessage = null;
        },
        getPaymentMethodsSuccess(state, action) {
            state.paymentMethodsLoading = false;
            state.currentPaymentMethods = action.payload;
        },
        getPaymentMethodsFailure(state, action) {
            state.paymentMethodsLoading = false;
            state.paymentMethodsErrorMessage = action.payload;
        },
        disableTestModeRequest(state, action) {
            state.disableTestModeLoading = true;
            state.disableTestModeErrorMessage = null;
        },
        disableTestModeSuccess(state, action) {
            let tempList = [...state.availableOrderInvoicingClients.existingConfigurations];
            let index = tempList.findIndex((item) => {
                return item.clientUUID === action.payload.associatedPlatformClientExternalUUID;
            });

            if (index >= 0) {
                tempList[index] = {
                    ...tempList[index],
                    testMode: action.payload.testMode,
                    globalStartDate: action.payload.globalStartDate,
                    refundsStartDate: action.payload.refundsStartDate,
                    creditNoteStartDate: action.payload.creditNoteStartDate,
                };
                state.availableOrderInvoicingClients = {
                    ...state.availableOrderInvoicingClients,
                    existingConfigurations: tempList,
                };
            }

            state.disableTestModeModal = false;
            state.disableTestModeLoading = false;
            state.configurationToDisableTestMode = null;

            mergeOrderInvoicingConfiguration(state, action.payload);
        },
        disableTestModeFailure(state, action) {
            state.disableTestModeLoading = false;
            state.disableTestModeErrorMessage = action.payload;
        },
        disableLiveModeRequest(state, action) {
            state.disableLiveModeLoading = true;
            state.disableLiveModeErrorMessage = null;
        },

        disableLiveModeSuccess(state, action) {
            let tempList = [...state.availableOrderInvoicingClients.existingConfigurations];
            let index = tempList.findIndex((item) => {
                return item.clientUUID === action.payload.associatedPlatformClientExternalUUID;
            });

            if (index >= 0) {
                tempList[index] = {
                    ...tempList[index],
                    testMode: action.payload.testMode,
                    globalStartDate: action.payload.globalStartDate,
                    refundsStartDate: action.payload.refundsStartDate,
                    creditNoteStartDate: action.payload.creditNoteStartDate,
                };
                state.availableOrderInvoicingClients = {
                    ...state.availableOrderInvoicingClients,
                    existingConfigurations: tempList,
                };
            }

            state.disableLiveModeModal = false;
            state.disableLiveModeLoading = false;
            state.configurationToDisableLiveMode = null;

            mergeOrderInvoicingConfiguration(state, action.payload);
        },
        disableLiveModeFailure(state, action) {
            state.disableLiveModeLoading = false;
            state.disableLiveModeErrorMessage = action.payload;
        },
        getDocumentPreviewImageRequest(state, action) {
            state.documentPreviewErrorMessage = null;
            state.documentPreviewLoading = true;
            state.lastRequestId = action.payload;
        },
        getDocumentPreviewImageSuccess(state, action) {
            if (state.lastRequestId === action.payload?.requestId) {
                state.documentPreviewLoading = false;
                if (action.payload?.data && typeof URL !== "undefined" && typeof URL.createObjectURL === "function") {
                    if (state.documentPreviewImage && typeof URL.revokeObjectURL === "function") {
                        URL.revokeObjectURL(state.documentPreviewImage);
                    }
                    state.documentPreviewImage = URL.createObjectURL(action.payload.data);
                }
            }
        },
        getDocumentPreviewImageFailure(state, action) {
            if (state.lastRequestId === action.payload?.requestId) {
                state.documentPreviewErrorMessage = action.payload.error;
                state.documentPreviewLoading = false;
            }
        },

        getPortalConfigRequest(state) {
            state.portalConfigLoading = true;
            state.portalConfigErrorMessage = null;
        },
        getPortalConfigSuccess(state, action) {
            state.portalConfigLoading = false;
            // Ignore responses that arrived after the user switched to a different OIC
            if (state.editConfiguration?.externalUUID === action.payload.oicUUID) {
                state.portalConfiguration = action.payload.portalConfig;
            }
        },
        getPortalConfigFailure(state, action) {
            state.portalConfigLoading = false;
            state.portalConfigErrorMessage = action.payload;
        },

        savePortalConfigRequest(state) {
            state.portalConfigLoading = true;
            state.portalConfigErrorMessage = null;
            state.portalConfigSuccessMessage = null;
        },
        savePortalConfigSuccess(state, action) {
            state.portalConfigLoading = false;
            state.portalConfiguration = action.payload;
            state.portalConfigSuccessMessage = "Kundenportal-Einstellungen gespeichert";
        },
        savePortalConfigFailure(state, action) {
            state.portalConfigLoading = false;
            state.portalConfigErrorMessage = action.payload;
        },

        uploadPortalBackgroundImageRequest(state) {
            state.portalBackgroundImageLoading = true;
            state.portalBackgroundImageErrorMessage = null;
        },
        uploadPortalBackgroundImageSuccess(state, action) {
            state.portalBackgroundImageLoading = false;
            state.uploadedPortalBackgroundImageFilename = action.payload;
        },
        uploadPortalBackgroundImageFailure(state, action) {
            state.portalBackgroundImageLoading = false;
            state.portalBackgroundImageErrorMessage = action.payload;
        },
    },
});

export default pathwayBillingConfigurationSlice;
